export type SystemUpdateLogScope = 'platform' | 'crm' | 'erp' | 'ops';

export interface SystemUpdateLogEntry {
  id: string;
  version: string;
  date: string;
  scope: SystemUpdateLogScope;
  publisherZh: string;
  publisherEn: string;
  titleZh: string;
  titleEn: string;
  summaryZh: string;
  summaryEn: string;
  changesZh: string[];
  changesEn: string[];
}

export const systemUpdateLogEntries: SystemUpdateLogEntry[] = [
  {
    id: '2026-07-17-admin-update-log',
    version: '2026.07.17',
    date: '2026-07-17',
    scope: 'platform',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: '系统设置增加更新日志',
    titleEn: 'Added system update log',
    summaryZh: '在系统设置的日志管理中增加更新日志入口，用于集中记录用户可感知的系统版本变化。',
    summaryEn:
      'Added an Update Log entry under Log Management in System Settings to track user-visible system changes.',
    changesZh: [
      '新增“更新日志”页面，按时间展示版本、影响范围、更新摘要和主要变更。',
      '新增“日志管理 / 更新日志”菜单入口，系统管理员可从统一后台查看系统发布记录。',
      '更新日志数据集中维护，后续发布可追加条目，避免版本说明散落在文档和聊天记录中。',
    ],
    changesEn: [
      'Added an Update Log page with version, scope, summary, and change details.',
      'Added a Log Management / Update Log menu entry for system administrators.',
      'Centralized update log entries so future releases can append user-facing release notes.',
    ],
  },
  {
    id: '2026-07-10-crm-data-permission',
    version: '2026.07.10',
    date: '2026-07-10',
    scope: 'crm',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: 'CRM 数据范围改为以 CRM 业务组织为准',
    titleEn: 'CRM data scope now follows CRM business orgs',
    summaryZh:
      '销售中心的数据权限判断收敛到 CRM 业务组织，减少企业 BU 与 CRM 组织不一致造成的可见性问题。',
    summaryEn:
      'CRM visibility now follows CRM business organizations to reduce mismatches with enterprise BUs.',
    changesZh: [
      '客户、联系人、线索、商机的数据范围优先按 CRM 业务组织计算。',
      '历史负责人和离职用户相关数据支持通过授权范围继续被特定角色查看。',
      '审批范围逻辑明确为：有审批范围配置时按配置，无配置时回退到数据范围。',
    ],
    changesEn: [
      'Accounts, contacts, leads, and opportunities now prioritize CRM business orgs for visibility.',
      'Historical owner data can remain visible through configured role scopes.',
      'Approval scope follows explicit approval configuration first, then falls back to data scope.',
    ],
  },
  {
    id: '2026-07-08-crm-option-config',
    version: '2026.07.08',
    date: '2026-07-08',
    scope: 'crm',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: 'CRM 业务选项配置上线',
    titleEn: 'CRM business option configuration',
    summaryZh:
      '客户行业/市场细分、销售方式等业务选项改为从配置表读取，并支持中英文显示。',
    summaryEn:
      'Business options such as market segment and sales type now read from configurable option tables with Chinese and English labels.',
    changesZh: [
      '客户行业/市场细分和销售方式支持按国内、海外范围配置。',
      '业务选项支持启用、停用、编辑和删除，减少硬编码选项维护成本。',
      '中文模式显示中文名称，英文模式显示英文名称。',
    ],
    changesEn: [
      'Market segments and sales types can be configured for domestic and overseas scopes.',
      'Options support enable, disable, edit, and delete actions.',
      'Chinese and English labels are shown according to the current language.',
    ],
  },
  {
    id: '2026-07-05-agent-sap-read-tools',
    version: '2026.07.05',
    date: '2026-07-05',
    scope: 'platform',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: 'AI 工作台接入 SAP 只读工具',
    titleEn: 'AI Workbench SAP read tools',
    summaryZh: 'AI 工作台增加 SAP 库存、物料和销售单查询工具，并通过独立权限控制访问。',
    summaryEn:
      'AI Workbench added SAP stock, material, and sales document read tools with explicit permission control.',
    changesZh: [
      '新增 SAP 库存查询、物料查询、销售单查询三个只读工具。',
      '工具调用经过 CRM API 和统一网关，调用记录进入集成日志。',
      '新增 sap.read 权限，用于区分普通智能体执行权限和 SAP 查询权限。',
    ],
    changesEn: [
      'Added read-only tools for SAP stock, material, and sales document queries.',
      'Tool calls go through CRM API and the unified gateway, with integration logs recorded.',
      'Added sap.read permission to separate SAP read access from general agent execution.',
    ],
  },
  {
    id: '2026-06-18-admin-log-management',
    version: '2026.06.18',
    date: '2026-06-18',
    scope: 'platform',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: '系统设置增加日志管理',
    titleEn: 'Added Log Management to System Settings',
    summaryZh: '系统设置中新增日志管理分组，集中承载登录日志、全局审计日志和集成日志。',
    summaryEn:
      'System Settings added a Log Management group for login logs, audit logs, and integration logs.',
    changesZh: [
      '新增登录日志，记录账号密码和钉钉登录的成功、失败、IP、设备和登录地址。',
      '全局审计日志和集成日志移动到日志管理分组下。',
      '日志入口继承后台角色菜单权限，避免普通用户误入运维页面。',
    ],
    changesEn: [
      'Added login logs with result, IP, device, and location details.',
      'Moved audit and integration logs under the Log Management group.',
      'Log entries inherit admin menu permissions.',
    ],
  },
  {
    id: '2026-05-08-v1',
    version: 'v1.0.0',
    date: '2026-05-08',
    scope: 'platform',
    publisherZh: '刘亚运',
    publisherEn: 'Liu Yayun',
    titleZh: 'CREST v1.0 首个正式版本',
    titleEn: 'CREST v1.0 initial release',
    summaryZh: '交付销售中心核心业务、系统设置、权限、审计、SAP/OA 集成和实时通知基础能力。',
    summaryEn:
      'Delivered core Sales Hub workflows, System Settings, permissions, audit, SAP/OA integration, and realtime notifications.',
    changesZh: [
      '上线客户、联系人、线索、商机、报价、项目交付等 CRM 核心业务。',
      '上线四档数据权限、字段安全、平台角色权限、应用中心和系统设置。',
      '上线审计日志、集成日志、事件总线、附件、导出和实时通知基础能力。',
    ],
    changesEn: [
      'Released core CRM entities: accounts, contacts, leads, opportunities, quotes, and delivery projects.',
      'Released data depth permissions, field security, platform roles, app center, and system settings.',
      'Released audit logs, integration logs, event bus, attachments, export, and realtime notifications.',
    ],
  },
];
